<template>
  <div class="container">
  <div class="ai-insights">
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h5 class="mb-0">
        <span class="emoji">🔍</span> Inventory Insights
        <span class="text-muted fs-6">(AI powered)</span>
      </h5>
    </div>

    <!-- NEW ANALYSIS -->
    <div>
    <!-- prompt chips -->
    <div class="prompt-chips mb-3">
      <button
        v-for="p in prompts"
        :key="p.key"
        class="chip"
        :class="{
          active: promptKey === p.key,
          'chip-govt-report': p.key === 'ethiopian-govt-report',
          'chip-audit': p.key === 'govt-audit-ethiopia'
        }"
        @click="runInsight(p.key)"
      >
        {{ p.label }}
      </button>
    </div>

    <!-- loading skeleton -->
    <div v-if="loading" class="skeleton-card">
      <div class="skeleton-line w-50" />
      <div class="skeleton-line w-80" />
      <div class="skeleton-line w-40" />
    </div>

    <!-- answer card -->
    <div v-else-if="result" class="answer-card">
      <div class="answer-header">
        <span class="badge bg-primary">{{ promptLabel }}</span>
        <div>
          <button class="btn btn-sm btn-outline-secondary me-2" @click="printResult">🖨️ Print</button>
          <button class="btn btn-sm btn-link" @click="showRaw = !showRaw">
            {{ showRaw ? 'Hide' : 'Show' }} snapshot
          </button>
        </div>
      </div>

      <!-- markdown table rendered safely -->
      <div class="answer-body" v-html="renderedMarkdown"></div>

      <!-- collapsible raw data -->
      <transition name="fade">
        <pre v-if="showRaw" class="raw-data">{{ snapshot }}</pre>
      </transition>
    </div>

    <!-- empty state -->
    <div v-else class="empty-card">
      <span class="emoji">🤖</span>
      <p>Choose an insight above — the AI will analyse your latest data.</p>
      <p class="amharic">ከላይ የሚወዱትን እንዲገምግሙ ይምረጡ።</p>
    </div>
    </div> <!-- END NEW ANALYSIS -->

  </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { marked } from 'marked'
import api from '@/api/axios'

/* ---------- state ---------- */
const promptKey = ref('')
const promptLabel = ref('')
const result = ref('')
const snapshot = ref({})
const loading = ref(false)
const showRaw = ref(false)

/* ---------- prompt menu ---------- */
const prompts = [
  //{ key: 'ethiopian-govt-report', label: '🇪🇹 Ethiopian Government Report' },
  //{ key: 'govt-audit-ethiopia', label: '🔎 Government Audit (Ethiopia)' },
  { key: 'forecast-4-weeks', label: '4-Week Sales Forecast' },
  { key: 'safety-stock', label: 'Safety Stock Advice' },
  { key: 'abc-analysis', label: 'ABC Sales Classification' },
  { key: 'dead-stock', label: 'Dead Stock (>90 Days)' },
  { key: 'supplier-risk', label: 'Supplier Delay Ranking' },
  { key: 'promo-uplift', label: 'Promotion Uplift Analysis' },
  { key: 'eoq-compare', label: 'EOQ vs AI Order Size' },
  { key: 'shelf-camera-audit', label: 'Shelf Photo Audit (Missing/Extra)' },
  { key: 'supplier-risk-map', label: 'Supplier Risk Heat-Map' },
  { key: 'cashflow-simulation', label: 'Inventory Cashflow Simulation' },
  { key: 'model-benchmark', label: 'Forecast Model Winner' },
  { key: 'auto-purchase-orders', label: 'Auto Purchase Order Suggestion' }
]

/* ---------- methods ---------- */
async function runInsight(key) {
  promptKey.value = key
  promptLabel.value = prompts.find(p => p.key === key).label
  loading.value = true
  result.value = ''
  snapshot.value = {}

  try {
    const { data } = await api.post('/ai/full/', { analyzType: key })
    result.value = data.result
    snapshot.value = data.snapshot
  } catch (e) {
    result.value = `❌ ${e.response?.data?.detail || 'Network error'}`
  } finally {
    loading.value = false
  }
}

/* ---------- computed ---------- */
const renderedMarkdown = computed(() => {
  if (!result.value) return ''
  return marked(result.value)
})

/* ---------- toggles ---------- */
function printResult() {
  const printContent = document.querySelector('.answer-card').innerHTML;
  const originalContent = document.body.innerHTML;

  document.body.innerHTML = printContent;
  window.print();
  document.body.innerHTML = originalContent;
  window.location.reload(); // restore Vue app
}

</script>

<style scoped>
/* ---------- layout ---------- */
.ai-insights {
  max-width: 900px;
  margin: 0 auto;
}

/* ---------- chips ---------- */
.prompt-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}
.chip {
  padding: 0.5rem 1rem;
  border: 1px solid #dee2e6;
  background: #fff;
  border-radius: 2rem;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s;
}
.chip:hover {
  border: 1px solid #0d6efd;
  color: #0d6efd;
}
.chip.active {
  background: #0d6efd;
  color: #fff;
  border-color: #0d6efd;
}
.chip-audit {
  border-color: #198754;
  color: #198754;
  font-weight: 500;
}
.chip-audit:hover {
  background: #198754;
  color: #fff;
}
.chip-audit.active {
  background: #198754;
  border-color: #198754;
  color: #fff;
}
.chip-govt-report {
  border-color: #b45309;
  color: #b45309;
  font-weight: 600;
  background: #fffbeb;
}
.chip-govt-report:hover {
  background: #b45309;
  color: #fff;
  border-color: #b45309;
}
.chip-govt-report.active {
  background: #b45309;
  border-color: #b45309;
  color: #fff;
}

/* ---------- skeleton ---------- */
.skeleton-card {
  background: #fff;
  border-radius: 0.5rem;
  padding: 1.5rem;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
}
.skeleton-line {
  height: 1rem;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: pulse 1.5s infinite;
  margin-bottom: 0.5rem;
  border-radius: 0.25rem;
}
@keyframes pulse {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* ---------- answer card ---------- */
.answer-card {
  background: #fff;
  border-radius: 0.5rem;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  padding: 1.5rem;
}
.answer-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}
.answer-body {
  font-size: 0.95rem;
  line-height: 1.6;
}
.answer-body :deep(table) {
  margin-top: 1rem;
  margin-bottom: 1rem;
  font-size: 0.85rem;
  width: 100%;
  border-collapse: collapse;
}
.answer-body :deep(th),
.answer-body :deep(td) {
  padding: 0.5rem 0.75rem;
  border: 1px solid #e9ecef;
}
.answer-body :deep(th) {
  background-color: #f8f9fa;
  font-weight: 600;
}
.answer-body :deep(h1),
.answer-body :deep(h2),
.answer-body :deep(h3) {
  margin-top: 1.5rem;
  margin-bottom: 0.75rem;
}

/* ---------- raw data ---------- */
.raw-data {
  margin-top: 1rem;
  background: #212529;
  color: #f8f9fa;
  padding: 1rem;
  border-radius: 0.4rem;
  font-size: 0.75rem;
  overflow-x: auto;
  max-height: 400px;
}

/* ---------- empty state ---------- */
.empty-card {
  text-align: center;
  padding: 3rem 1rem;
  background: #fff;
  border-radius: 0.5rem;
  border: 2px dashed #dee2e6;
  color: #6c757d;
}
.empty-card .emoji {
  font-size: 2rem;
}
.empty-card .amharic {
  font-size: 0.9rem;
  margin-top: 0.5rem;
}

/* ---------- transitions ---------- */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.history-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
}
.history-filters .form-select,
.history-filters .form-control {
  flex: 1;
  min-width: 160px;
  max-width: 260px;
}

.date-bar {
  display: flex;
  gap: 0.75rem;
  align-items: flex-end;
  background: #fff;
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
}
.date-chip {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}
.date-chip label {
  font-size: 0.75rem;
  color: #6c757d;
  margin: 0;
}
.date-chip input {
  width: 140px;
}

.answer-card {
  position: relative;
  border: 1px solid rgba(0, 123, 255, 0.4);
  box-shadow: 0px 0px 18px rgba(0, 123, 255, 0.3);
  transition: all 0.4s ease-in-out;
}

.answer-card:hover {
  box-shadow: 0px 0px 24px rgba(0, 123, 255, 0.6);
  border-color: rgba(0, 123, 255, 0.8);
}

</style>